import { useState, useEffect } from 'react';
import Axios from 'axios';
import { getAuthHeader } from './authToken';

// Hook to fetch test categories from the API
export const useTestCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/test-categories`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            // Transform API response to match the expected format
            const formattedCategories = response.data.map((category) => ({
                value: category.name,
                label: category.label,
                id: category.id,
                has_special_choices: category.has_special_choices,
                special_choices_config: category.special_choices_config,
                section: [
                    {
                        value: "With Section",
                        label: "With Section",
                        // For categories with special choices (like ACT), add numChoices
                        ...(category.has_special_choices && category.special_choices_config && {
                            numChoices: Object.keys(category.special_choices_config).map((key) => ({
                                value: key,
                                label: key,
                            })),
                        }),
                    },
                    {
                        value: "No Section",
                        label: "No Section",
                    },
                ],
            }));

            setCategories(formattedCategories);
            setError(null);
        } catch (err) {
            console.error('Error fetching test categories:', err);
            setError(err);
            // Fallback to default categories if API fails
            setCategories(getDefaultCategories());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    return { categories, loading, error, refetch: fetchCategories };
};

// Default categories as fallback
const getDefaultCategories = () => {
    return [
        {
            value: "SAT",
            label: "SAT",
            section: [
                { value: "With Section", label: "With Section" },
                { value: "No Section", label: "No Section" },
            ],
        },
        {
            value: "ACT",
            label: "ACT",
            has_special_choices: true,
            section: [
                {
                    value: "With Section",
                    label: "With Section",
                    numChoices: [
                        { value: "4", label: "4" },
                        { value: "5", label: "5" },
                    ],
                },
                { value: "No Section", label: "No Section" },
            ],
        },
        {
            value: "SSAT",
            label: "SSAT",
            section: [
                { value: "With Section", label: "With Section" },
                { value: "No Section", label: "No Section" },
            ],
        },
        {
            value: "ISEE",
            label: "ISEE",
            section: [
                { value: "With Section", label: "With Section" },
                { value: "No Section", label: "No Section" },
            ],
        },
        {
            value: "Vocab",
            label: "Vocab",
            section: [
                { value: "With Section", label: "With Section" },
                { value: "No Section", label: "No Section" },
            ],
        },
    ];
};

// Legacy function for backward compatibility
// Components that use this will gradually migrate to useTestCategories hook
export const getTestCategory = () => {
    return getDefaultCategories();
};

// Helper function to get special choices for a category (like ACT)
export const getSpecialChoices = (category, numChoices, questionIndex) => {
    if (!category || !category.has_special_choices || !category.special_choices_config) {
        return null;
    }

    const config = category.special_choices_config[numChoices];
    if (!config) return null;

    // Alternate between odd and even patterns based on question index
    return questionIndex % 2 === 0 ? config.odd : config.even;
};
